import { useCallback, useEffect, useState } from 'react';
import { eventsApi } from '../../../api/eventsApi';
import { isRunningStatus } from '../../../constants/jobStatus';

/** The current report if still listed, else the official, else the first completed one. */
const pickDefaultReportId = (catalog, currentId = null) => {
  const reports = catalog?.reports || [];
  if (currentId && reports.some((r) => r.id === currentId)) return currentId;
  return catalog?.official_full_id
    || reports.find((r) => r.is_official)?.id
    || reports.find((r) => r.status === 'COMPLETED')?.id
    || null;
};

/** Stored event reports, the selected one, and its content once it is completed. */
const useEventReport = (eventId) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [catalog, setCatalog] = useState({ reports: [] });
  const [selectedReportId, setSelectedReportId] = useState(null);

  const load = useCallback(async (preferredId = null) => {
    if (!eventId) return;
    setLoading(true);
    setError('');
    try {
      const nextCatalog = await eventsApi.listReports(eventId).catch(() => null);
      let reportId = preferredId;
      if (nextCatalog) {
        setCatalog(nextCatalog);
        reportId = pickDefaultReportId(nextCatalog, preferredId);
        setSelectedReportId(reportId);
      }

      const status = await eventsApi.getReportStatus(eventId, reportId);
      if (!status || status.status === 'NO_REPORT') {
        setReport(null);
      } else if (isRunningStatus(status.status)) {
        setError('Report is being generated. Please wait...');
      } else if (status.status === 'FAILED') {
        setError(status.error_message || 'Report generation failed');
      } else if (status.status === 'COMPLETED') {
        const response = await eventsApi.getReport(eventId, reportId || status.report_id);
        setReport(response.data || response);
      }
    } catch (err) {
      if (err.status === 404) {
        setReport(null);
      } else {
        setError(err.message || 'Failed to load report');
      }
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    load();
  }, [load]);

  const selectReport = async (reportId) => {
    setSelectedReportId(reportId);
    await load(reportId);
  };

  return { report, loading, error, catalog, selectedReportId, selectReport, reload: load };
};

export default useEventReport;
